import React, { useContext, useEffect } from "react";
import {
  EllipticalROITool,
  LengthTool,
  PanTool,
  RectangleROITool,
  StackScrollTool,
  WindowLevelTool,
  ZoomTool,
} from "@cornerstonejs/tools";
import ViewportContext from "../../../context/studyviewer/ViewportContext";
import { reset, eventBus } from "./CornerstoneViewportService";
import studyviewerContext from "../../../context/studyviewer/StudyviewerContext";
import ThumbnailContext from "../../../context/studyviewer/ThumbnailContext";
import studyReportContext from "../../../context/studyreport/StudyReportContext";
import {
  getBasicViewerContextActionShortcut,
  getBasicViewerToolShortcutLabel,
} from "./basicViewerToolShortcuts";
import { MOUSE_ASSIGNABLE_TOOL_NAMES } from "../../../constants/MouseButtonToolAssignments";

const ContextMenuRow = ({ label, shortcut, onClick, className = "" }) => (
  <div
    className={`context-menu-row ${className}`.trim()}
    onClick={onClick}
    role="presentation"
  >
    <span className="context-menu-row-label">{label}</span>
    {shortcut ? (
      <span className="context-menu-row-shortcut">{shortcut}</span>
    ) : null}
  </div>
);

const ContextMenu = (props) => {
  const {
    handleRemoveBoundingBox,
    setActiveTool,
    setPreviousActiveTool,
    activeTool,
    setContextMenuOpen,
    contextMenuOpen,
    activeViewportIndex,
    annotationData,
    annotationDataAI,
    addedAnnotation,
    isMPRActive,
    setPreviousActiveMPRTool,
    activeMPRTool,
    setActiveMPRTool,
    handleChangeLabelForTextAnnotation,
    activeMPRViewportIndex,
    boundingBoxMode,
    setBoundingBoxMode,
    isCropEnable,
    setIsCropEnable,
    viewportSeriesData,
    removeSelectedMeasurement,
    removeAllMeasurements,
    assignMouseButtonToAssignableTool,
  } = useContext(ViewportContext);
  const { showTextAnnotation } = useContext(studyReportContext) || {};
  const { checkForMaskingAnnotation, maskImages, setMaskImages} = useContext(studyviewerContext);
  const {activeThumbnailIndex} = useContext(ThumbnailContext);
  const closeContextMenu = () => {
    setContextMenuOpen(() => {
      const newVal = { ...contextMenuOpen };
      newVal.open = false;
      newVal.mouseEvt = null;
      newVal.data = null;
      return newVal;
    });
  };

  const changeActiveTool = async (tool) => {
    // eventTarget.reset();
    if (isMPRActive) {
      setPreviousActiveMPRTool(activeMPRTool);
      if (MOUSE_ASSIGNABLE_TOOL_NAMES.includes(tool)) {
        assignMouseButtonToAssignableTool("primary", tool, true);
      }
      setActiveMPRTool(tool);
    } else {
      const isBBMode = boundingBoxMode;
      const isCropMode =  isCropEnable;

      setIsCropEnable(false);
      setBoundingBoxMode(false);
    
    if (!isBBMode && !isCropMode && activeTool === tool) {
        setPreviousActiveTool(activeTool);
        setActiveTool(StackScrollTool.toolName);
      } else {
        setPreviousActiveTool(activeTool);
        if (MOUSE_ASSIGNABLE_TOOL_NAMES.includes(tool)) {
          assignMouseButtonToAssignableTool("primary", tool, true);
        }
        setActiveTool(tool);
      }
    }
    setContextMenuOpen(() => {
      const newVal = { ...contextMenuOpen };
      newVal.open = false;
      newVal.mouseEvt = null;
      newVal.data = null;
      return newVal;
    });
  };

  const handleReset = async () => {
   const activeIndex =  isMPRActive ? activeMPRViewportIndex : activeViewportIndex
 
    await reset(
      activeIndex,
      annotationData,
      annotationDataAI,
      addedAnnotation,
      isMPRActive,
      maskImages,
      activeThumbnailIndex
    );
    setMaskImages(maskImages);
    setContextMenuOpen(() => {
      const newVal = { ...contextMenuOpen };
      newVal.open = false;
      newVal.mouseEvt = null;
      newVal.data = null;
      return newVal;
    });
    // TODO: reset need to discuss
    // setCropAnnotationData([]);
  };

  useEffect(() => {
    const contextMenu = document.getElementById("context-menu");

    if (!contextMenu) return;
    contextMenu.style.visibility = "hidden";
    contextMenu.style.display = "block";
    const { clientX, clientY } = props.mouseEvt;
    const menuWidth = contextMenu.offsetWidth;
    const menuHeight = contextMenu.offsetHeight;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Calculate the adjusted position
    let left = clientX;
    let top = clientY;

    // Prevent menu from going off the right edge
    if (clientX + menuWidth > screenWidth) {
      left = screenWidth - menuWidth;
    }

    // Prevent menu from going off the bottom edge
    if (clientY + menuHeight > screenHeight) {
      top = screenHeight - menuHeight;
    }

    contextMenu.style.left = `${left}px`;
    contextMenu.style.top = `${top}px`;
    contextMenu.style.visibility = "visible";
    contextMenu.classList.add("visible");
  }, [props.mouseEvt]);

  // todo : - 
  const checkForMasking= ()=>{
    return checkForMaskingAnnotation(props.data?.annotationUID) !==null;
  }
 
  return (
    <div id="context-menu">
     { !checkForMasking()&&(
        <>
          <ContextMenuRow
            label="Window Level Tool"
            shortcut={getBasicViewerToolShortcutLabel(WindowLevelTool.toolName)}
            onClick={() => changeActiveTool(WindowLevelTool.toolName)}
          />
          <ContextMenuRow
            label="Pan Tool"
            shortcut={getBasicViewerToolShortcutLabel(PanTool.toolName)}
            onClick={() => changeActiveTool(PanTool.toolName)}
          />
          <ContextMenuRow
            label="Zoom Tool"
            shortcut={getBasicViewerToolShortcutLabel(ZoomTool.toolName)}
            onClick={() => changeActiveTool(ZoomTool.toolName)}
          />
          <ContextMenuRow
            label="Length Tool"
            shortcut={getBasicViewerToolShortcutLabel(LengthTool.toolName)}
            onClick={() => changeActiveTool(LengthTool.toolName)}
          />
          <ContextMenuRow
            label="Ellipse Tool"
            shortcut={getBasicViewerToolShortcutLabel(EllipticalROITool.toolName)}
            onClick={() => changeActiveTool(EllipticalROITool.toolName)}
          />
          <ContextMenuRow
            label="Rectangle Tool"
            shortcut={getBasicViewerToolShortcutLabel(RectangleROITool.toolName)}
            onClick={() => changeActiveTool(RectangleROITool.toolName)}
          />

      </>)}
      <ContextMenuRow
        className="context-menu-section-divider"
        label="Delete Selected"
        shortcut={getBasicViewerContextActionShortcut("deleteSelected")}
        onClick={() => {
          removeSelectedMeasurement();
          closeContextMenu();
        }}
      />
      <ContextMenuRow
        label="Delete All"
        shortcut={getBasicViewerContextActionShortcut("deleteAll")}
        onClick={() => {
          removeAllMeasurements();
          closeContextMenu();
        }}
      />
      {props?.data?.metadata?.toolName == "ArrowAnnotate" && showTextAnnotation && (
        <ContextMenuRow
          label="Change Label"
          onClick={() => {
            handleChangeLabelForTextAnnotation(props.data);
          }}
        />
      )}
      <ContextMenuRow
        label="Reset"
        shortcut={getBasicViewerToolShortcutLabel("Reset")}
        onClick={handleReset}
      />
    </div>
  );
};

export default ContextMenu;
